const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

/** Backend down, deploy, restart, or non-JSON error page (e.g. nginx HTML). */
export const SERVICE_UNAVAILABLE_CODE = 'SERVICE_UNAVAILABLE';

/** Extract WH JSON `message` from a long sync error string. */
export function extractWhApiMessage(text) {
  if (typeof text !== 'string' || !text.trim()) return null;
  const raw = text.trim();
  const brace = raw.indexOf('{');
  if (brace >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(brace));
      if (parsed && typeof parsed.message === 'string' && parsed.message.trim()) {
        return parsed.message.trim();
      }
    } catch {
      /* fall through */
    }
  }
  const m = raw.match(/"message"\s*:\s*"((?:\\.|[^"\\])*)"/);
  if (m && m[1]) {
    return m[1].replace(/\\"/g, '"').trim();
  }
  return null;
}

/** Human-readable message from API error payload (ignores boolean `error: true`). */
export function getApiErrorMessage(err, fallback = 'API Error') {
  const data = err?.data;
  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message.trim();
  }
  if (typeof data?.errorText === 'string' && data.errorText.trim()) {
    const whMsg = extractWhApiMessage(data.errorText);
    if (whMsg) return whMsg;
    return data.errorText.trim();
  }
  if (typeof data?.detail === 'string' && data.detail.trim()) {
    return data.detail.trim();
  }
  if (typeof data?.error === 'string' && data.error.trim()) {
    return data.error.trim();
  }
  const msg = err?.message;
  if (typeof msg === 'string' && msg.trim() && msg !== 'true' && msg !== 'false') {
    const whMsg = extractWhApiMessage(msg);
    if (whMsg) return whMsg;
    return msg.trim();
  }
  return fallback;
}

class ApiError extends Error {
  constructor(status, data = {}) {
    super(
      getApiErrorMessage(
        { data },
        data?.code === SERVICE_UNAVAILABLE_CODE ? 'Service unavailable' : 'API Error',
      ),
    );
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.code = data?.code;
  }
}

/** True when JWT is invalid or expired (safe to clear session). */
export function isAuthFailureError(err) {
  return err?.name === 'ApiError' && err.status === 401;
}

/** Network failure, timeout, or gateway errors during maintenance / restart. */
export function isServiceUnavailableError(err) {
  if (err?.name !== 'ApiError') return false;
  if (err.status === 0) return true;
  if (err.data?.code === SERVICE_UNAVAILABLE_CODE) return true;
  if ([502, 503, 504].includes(err.status)) return true;
  return false;
}

async function readResponseJson(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    if (!response.ok) {
      throw new ApiError(response.status, { code: SERVICE_UNAVAILABLE_CODE });
    }
    return {};
  }
}

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  getToken() {
    // Session token is tab-scoped and must always override localStorage.
    // This guarantees "enter as client" uses the client JWT even when admin
    // token is persisted in localStorage with "remember me".
    const sessionToken = sessionStorage.getItem('token');
    if (sessionToken) return sessionToken;
    return localStorage.getItem('token');
  }

  setToken(token, rememberMe = false) {
    if (rememberMe) {
      localStorage.setItem('token', token);
    } else {
      sessionStorage.setItem('token', token);
    }
  }

  removeToken() {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
  }

  setUser(user, rememberMe = false) {
    const userJson = JSON.stringify(user);
    if (rememberMe) {
      localStorage.setItem('user', userJson);
    } else {
      sessionStorage.setItem('user', userJson);
    }
  }

  getUser() {
    if (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem('impersonation_original_token')
    ) {
      const u = sessionStorage.getItem('user');
      if (u) return JSON.parse(u);
    }
    const userJson = localStorage.getItem('user') || sessionStorage.getItem('user');
    return userJson ? JSON.parse(userJson) : null;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Remove Content-Type for blob requests
    if (options.responseType === 'blob') {
      delete headers['Content-Type'];
    }

    let response;
    try {
      response = await fetch(url, {
        ...options,
        headers,
      });
    } catch {
      throw new ApiError(0, { code: SERVICE_UNAVAILABLE_CODE });
    }

    // Handle blob response
    if (options.responseType === 'blob') {
      if (!response.ok) {
        let errorData;
        try {
          errorData = await readResponseJson(response);
        } catch (e) {
          if (e instanceof ApiError) throw e;
          errorData = { code: SERVICE_UNAVAILABLE_CODE };
        }
        const payload = { ...errorData };
        if ([502, 503, 504].includes(response.status) && !payload.code) {
          payload.code = SERVICE_UNAVAILABLE_CODE;
        }
        throw new ApiError(response.status, payload);
      }
      return { data: await response.blob() };
    }

    const data = await readResponseJson(response);

    if (!response.ok) {
      const payload = { ...data };
      if ([502, 503, 504].includes(response.status) && !payload.code) {
        payload.code = SERVICE_UNAVAILABLE_CODE;
      }
      throw new ApiError(response.status, payload);
    }

    return data;
  }

  async requestFormData(endpoint, formData, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers = {
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        ...options,
        headers,
        body: formData,
      });
    } catch {
      throw new ApiError(0, { code: SERVICE_UNAVAILABLE_CODE });
    }

    const data = await readResponseJson(response);

    if (!response.ok) {
      const payload = { ...data };
      if ([502, 503, 504].includes(response.status) && !payload.code) {
        payload.code = SERVICE_UNAVAILABLE_CODE;
      }
      throw new ApiError(response.status, payload);
    }

    return data;
  }

  async requestNoContent(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers = {
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response;
    try {
      response = await fetch(url, {
        ...options,
        headers,
      });
    } catch {
      throw new ApiError(0, { code: SERVICE_UNAVAILABLE_CODE });
    }

    if (!response.ok) {
      let data;
      try {
        data = await readResponseJson(response);
      } catch (e) {
        if (e instanceof ApiError) {
          throw e;
        }
        data = { code: SERVICE_UNAVAILABLE_CODE };
      }
      const payload = { ...data };
      if ([502, 503, 504].includes(response.status) && !payload.code) {
        payload.code = SERVICE_UNAVAILABLE_CODE;
      }
      throw new ApiError(response.status, payload);
    }

    return { success: true };
  }

  // Auth endpoints
  async register(userData) {
    return this.request('/auth/register/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(credentials, rememberMe = false) {
    const response = await this.request('/auth/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.data && response.data.token) {
      this.setToken(response.data.token, rememberMe);
      this.setUser(response.data.user, rememberMe);
    }

    return response;
  }

  async verifyEmail(token) {
    return this.request('/auth/verify-email/', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerification(email) {
    return this.request('/auth/resend-verification/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async requestPasswordReset(email) {
    return this.request('/auth/password-reset/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async confirmPasswordReset(token, newPassword, newPasswordConfirm) {
    return this.request('/auth/password-reset/confirm/', {
      method: 'POST',
      body: JSON.stringify({
        token,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      }),
    });
  }

  async changePassword(oldPassword, newPassword, newPasswordConfirm) {
    return this.request('/auth/password-change/', {
      method: 'POST',
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      }),
    });
  }

  async getProfile() {
    return this.request('/auth/profile/', {
      method: 'GET',
    });
  }

  async createSuperuser(body) {
    return this.request('/auth/superusers/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getImpersonateUsers(search = '') {
    const params = search ? { search } : {};
    const qs = new URLSearchParams(params).toString();
    const url = qs ? `/auth/impersonate/users/?${qs}` : '/auth/impersonate/users/';
    return this.request(url, { method: 'GET' });
  }

  async impersonate(userId) {
    return this.request('/auth/impersonate/', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }

  async updateProfile(userData) {
    return this.request('/auth/profile/', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  // Legal document endpoints
  async uploadDocument(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.requestFormData('/auth/documents/', formData);
  }

  async deleteDocument(documentId) {
    return this.requestNoContent(`/auth/documents/${documentId}/`, {
      method: 'DELETE',
    });
  }

  // Store endpoints
  async getStores() {
    return this.request('/stores/', {
      method: 'GET',
    });
  }

  async getSystemBulletins() {
    return this.request('/bulletins/', { method: 'GET' });
  }

  async markSystemBulletinRead(bulletinId) {
    return this.request(`/bulletins/${bulletinId}/read/`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  _billingQueryString(params = {}) {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val === undefined || val === null || val === '') return;
      if (key === 'store_ids' && Array.isArray(val)) {
        val.forEach((id) => usp.append('store_ids', String(id)));
        return;
      }
      usp.set(key, String(val));
    });
    return usp.toString();
  }

  async getBillingExpenses(params = {}) {
    const queryString = this._billingQueryString(params);
    const endpoint = queryString ? `/billing/expenses/?${queryString}` : '/billing/expenses/';
    return this.request(endpoint, { method: 'GET' });
  }

  async createBillingAlipayQr(storePk) {
    return this.request(`/billing/stores/${storePk}/alipay-qr/`, { method: 'POST' });
  }

  async createBillingAlipayPagePay(storePk) {
    return this.request(`/billing/stores/${storePk}/alipay-page-pay/`, { method: 'POST' });
  }

  async getBillingUnreadCount() {
    const data = await this.request('/billing/unread-count/', { method: 'GET' });
    return { data: data };
  }

  async billingMarkSeen() {
    return this.request('/billing/mark-seen/', { method: 'POST' });
  }

  async createBillingReceipt(orderId, receiptType, amountPerItem, options = {}) {
    const body = { order_id: orderId, receipt_type: receiptType };
    if (amountPerItem != null && amountPerItem !== '') {
      body.amount_per_item = amountPerItem;
    }
    if (options.addInspection === 'no_media' || options.addInspection === 'with_media') {
      body.add_inspection = options.addInspection;
    }
    if (Array.isArray(options.item_metrics) && options.item_metrics.length > 0) {
      body.item_metrics = options.item_metrics;
    }
    return this.request('/billing/receipt/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async postAdminOrderReceiptWbPreflight(orderId, options = {}) {
    const { signal, ...rest } = options;
    return this.request(`/admin/orders/${orderId}/receipt-wb-preflight/`, {
      method: 'POST',
      signal,
      ...rest,
    });
  }

  /** Superuser: live WB Content API — обновить превью-картинки позиций заказа. */
  async postAdminOrderWbRefreshImages(orderId) {
    return this.request(`/admin/orders/${orderId}/wb-refresh-images/`, { method: 'POST' });
  }

  /** Superuser: сгенерировать PDF-этикетку товаров из Warehouse Hub. */
  async postAdminOrderGenerateWhLabel(orderId) {
    return this.request(`/admin/orders/${orderId}/generate-wh-label/`, { method: 'POST' });
  }

  async createBillingPrepareReturn(orderId, amountPerItem) {
    return this.request('/billing/prepare-return/', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId, amount_per_item: amountPerItem }),
    });
  }

  async createBillingDisposal(orderId, amountsByItem) {
    return this.request('/billing/disposal/', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId, amounts_by_item: amountsByItem }),
    });
  }

  async getBillingExpense(id) {
    return this.request(`/billing/expenses/${id}/`, { method: 'GET' });
  }

  async updateBillingExpense(id, data) {
    return this.request(`/billing/expenses/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteBillingExpense(id) {
    return this.requestNoContent(`/billing/expenses/${id}/`, { method: 'DELETE' });
  }

  async getBillingAdminAnalytics(params = {}) {
    const queryString = this._billingQueryString(params);
    const endpoint = queryString
      ? `/billing/admin/analytics/?${queryString}`
      : '/billing/admin/analytics/';
    return this.request(endpoint, { method: 'GET' });
  }

  async getBillingAdminOrderDetail(params = {}) {
    const queryString = this._billingQueryString(params);
    const endpoint = queryString
      ? `/billing/admin/order-detail/?${queryString}`
      : '/billing/admin/order-detail/';
    return this.request(endpoint, { method: 'GET' });
  }

  async getBillingAdminStaffStatusAnalytics(params = {}) {
    const queryString = this._billingQueryString(params);
    const endpoint = queryString
      ? `/billing/admin/staff-status-analytics/?${queryString}`
      : '/billing/admin/staff-status-analytics/';
    return this.request(endpoint, { method: 'GET' });
  }

  async getBillingAdminStoreOwners(params = {}) {
    const queryString = this._billingQueryString(params);
    const endpoint = queryString
      ? `/billing/admin/store-owners/?${queryString}`
      : '/billing/admin/store-owners/';
    return this.request(endpoint, { method: 'GET' });
  }

  async billingExpensesBulkDelete(body) {
    return this.request('/billing/expenses/bulk-delete/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getBillingUserStores(userId) {
    const queryString = new URLSearchParams({ user_id: userId }).toString();
    return this.request(`/billing/user-stores/?${queryString}`, { method: 'GET' });
  }

  async createBillingTopUp(userId, amountsByStore, comment) {
    const body = { user_id: userId, amounts_by_store: amountsByStore };
    if (comment != null && String(comment).trim()) {
      body.comment = String(comment).trim();
    }
    return this.request('/billing/top-up/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async downloadBillingExpensesExcel(params = {}) {
    const queryString = this._billingQueryString(params);
    const endpoint = queryString ? `/billing/expenses/export/?${queryString}` : '/billing/expenses/export/';
    return this.request(endpoint, {
      method: 'GET',
      responseType: 'blob',
    });
  }

  async getBillingAccountsWeekSummary(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/billing/accounts/week-summary/?${queryString}`
      : '/billing/accounts/week-summary/';
    return this.request(endpoint, { method: 'GET' });
  }

  async getBillingAccountsByBarcode(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/billing/accounts/by-barcode/?${queryString}`
      : '/billing/accounts/by-barcode/';
    return this.request(endpoint, { method: 'GET' });
  }

  async postBillingAccountsBarcodeThumbnails(body) {
    return this.request('/billing/accounts/by-barcode/thumbnails/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getBillingAccountsBarcodeDetail(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/billing/accounts/by-barcode/detail/?${queryString}`
      : '/billing/accounts/by-barcode/detail/';
    return this.request(endpoint, { method: 'GET' });
  }

  async downloadBillingAccountsBarcodeExcel(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/billing/accounts/by-barcode/export/?${queryString}`
      : '/billing/accounts/by-barcode/export/';
    return this.request(endpoint, {
      method: 'GET',
      responseType: 'blob',
    });
  }

  async downloadBillingAccountsBarcodePdf(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `/billing/accounts/by-barcode/export-pdf/?${queryString}`
      : '/billing/accounts/by-barcode/export-pdf/';
    return this.request(endpoint, {
      method: 'GET',
      responseType: 'blob',
    });
  }

  async addStore(storeData) {
    return this.request('/stores/', {
      method: 'POST',
      body: JSON.stringify(storeData),
    });
  }

  async getStore(storeId) {
    return this.request(`/stores/${storeId}/`, {
      method: 'GET',
    });
  }

  async updateStore(storeId, storeData) {
    return this.request(`/stores/${storeId}/`, {
      method: 'PATCH',
      body: JSON.stringify(storeData),
    });
  }

  async deleteStore(storeId) {
    return this.requestNoContent(`/stores/${storeId}/`, {
      method: 'DELETE',
    });
  }

  async getStoreVerificationStatus(storeId) {
    return this.request(`/stores/${storeId}/verification/`, {
      method: 'GET',
    });
  }

  async updateStoreApiKey(storeId, apiKey) {
    return this.request(`/stores/${storeId}/api-key/`, {
      method: 'PATCH',
      body: JSON.stringify({ api_key: apiKey }),
    });
  }

  // Returns Table endpoints
  async getReturnsInTransit(params = {}) {
    // Default to page 1 and page_size 100 if not provided
    const queryParams = {
      page: 1,
      page_size: 100,
      ...params,
    };
    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/returns/in-transit/?${queryString}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async getReturnsOtherPvz(params = {}) {
    const queryParams = {
      page: 1,
      page_size: 100,
      ...params,
    };
    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/returns/other-pvz/?${queryString}`;
    return this.request(endpoint, { method: 'GET' });
  }

  async getReturnsInWarehouse(params = {}) {
    // Default to page 1 and page_size 100 if not provided
    const queryParams = {
      page: 1,
      page_size: 100,
      ...params,
    };
    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/returns/in-warehouse/?${queryString}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async getOrdersInProcessing(params = {}) {
    const queryParams = {
      page: 1,
      page_size: 100,
      ...params,
    };
    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/returns/in-processing/?${queryString}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async getOrdersPendingDecision(params = {}) {
    const queryParams = {
      page: 1,
      page_size: 100,
      ...params,
    };
    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/returns/pending-decision/?${queryString}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async getCompletedOrders(params = {}) {
    const queryParams = {
      page: 1,
      page_size: 100,
      ...params,
    };
    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/returns/completed/?${queryString}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async getAllReturns(params = {}) {
    const queryParams = {
      page: 1,
      page_size: 100,
      ...params,
    };
    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/returns/?${queryString}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async updateReturn(returnId, data) {
    return this.request(`/returns/${returnId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async bulkUpdateReturns(payload) {
    return this.request('/returns/bulk-update/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** До 10 OrderItem id за запрос — превью/большое фото WB (Content API). */
  async postReturnsOrderItemsWbMedia(orderItemIds) {
    return this.request('/returns/order-items/wb-media/', {
      method: 'POST',
      body: JSON.stringify({ order_item_ids: orderItemIds }),
    });
  }

  /** Superuser: до 10 OrderItem id — фоновая догрузка метрик/фото WB. */
  async postAdminOrderItemsWbMetrics(orderItemIds) {
    return this.request('/admin/order-items/wb-metrics/', {
      method: 'POST',
      body: JSON.stringify({ order_item_ids: orderItemIds }),
    });
  }

  async getReturnsWhBilling(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/returns/wh-billing/?${queryString}` : '/returns/wh-billing/';
    return this.request(endpoint, { method: 'GET' });
  }

  async getReturnsStatistics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/returns/statistics/?${queryString}` : '/returns/statistics/';
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async checkDeliveryAddress() {
    return this.request('/returns/check-delivery-address/', {
      method: 'GET',
    });
  }

  async addReturnAttachment(attachmentData) {
    return this.request('/returns/attachments/', {
      method: 'POST',
      body: JSON.stringify(attachmentData),
    });
  }

  async deleteReturnAttachment(attachmentId) {
    return this.request(`/returns/attachments/${attachmentId}/`, {
      method: 'DELETE',
    });
  }

  async patchReturnAttachmentComment(attachmentId, userComment) {
    return this.request(`/returns/attachments/${attachmentId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ user_comment: userComment }),
    });
  }

  // WB Data Sync endpoints
  async syncOrderWBData(orderId) {
    return this.request(`/returns/${orderId}/sync-wb/`, {
      method: 'POST',
    });
  }

  async batchSyncOrdersWBData(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/returns/sync-wb/?${queryString}` : '/returns/sync-wb/';
    return this.request(endpoint, {
      method: 'POST',
    });
  }

  // Admin order management endpoints
  async getAdminOrders(params = {}) {
    const queryParams = {
      page: 1,
      page_size: 100,
      ...params,
    };
    // Remove undefined/null values
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] === undefined || queryParams[key] === null || queryParams[key] === '') {
        delete queryParams[key];
      }
    });
    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/admin/orders/?${queryString}`;
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async getAdminOrder(orderId) {
    return this.request(`/admin/orders/${orderId}/`, { method: 'GET' });
  }

  async updateAdminOrder(orderId, data) {
    return this.request(`/admin/orders/${orderId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Admin: search stores (superuser only).
   * extras: { store_type?: string, has_api_key?: boolean }
   */
  async getAdminStoresSearch(q = '', limit = 50, extras = {}) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (limit != null) params.set('limit', String(limit));
    if (extras.store_type) params.set('store_type', extras.store_type);
    if (extras.has_api_key) params.set('has_api_key', '1');
    const qs = params.toString();
    return this.request(`/admin/stores/${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  /** Superuser admin console: run audited manage.py-equivalent command. */
  async adminConsoleRun({ command, store_id, only_summary = true }) {
    return this.request('/admin/console/run/', {
      method: 'POST',
      body: JSON.stringify({
        command,
        store_id: store_id || '',
        only_summary: Boolean(only_summary),
      }),
    });
  }

  /** Admin: find or create returns by shkId (all active verified stores). POST admin/returns/find-by-shk/ */
  async postAdminFindReturnsByShk(shkIds) {
    return this.request('/admin/returns/find-by-shk/', {
      method: 'POST',
      body: JSON.stringify({ shk_ids: shkIds }),
    });
  }

  /** Superuser: WB goods-return report for a store (JSON). */
  async getAdminWBStoreReturns(params = {}) {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
    });
    const qs = p.toString();
    return this.request(`/admin/wb-store-returns/${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  /** Superuser: same as getAdminWBStoreReturns but Excel (blob). */
  async downloadAdminWBStoreReturnsExcel(params = {}) {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
    });
    p.set('export', 'xlsx');
    return this.request(`/admin/wb-store-returns/?${p.toString()}`, {
      method: 'GET',
      responseType: 'blob',
    });
  }

  /** Superuser: create Order from WBGoodsReturnSnapshot row id. */
  async postAdminWBStoreReturnsCreateOrder(snapshotId) {
    return this.request(`/admin/wb-store-returns/${snapshotId}/create-order/`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  /** Delete order by id. DELETE orders/<id>/ */
  async deleteOrder(orderId) {
    return this.request(`/orders/${orderId}/`, { method: 'DELETE' });
  }

  // Support chat endpoints
  async getSupportChats(options = {}) {
    const params = new URLSearchParams();
    if (options.q) params.set('q', options.q);
    if (options.store_id) params.set('store_id', options.store_id);
    const qs = params.toString();
    const suffix = qs ? `?${qs}` : '';
    return this.request(`/support/chats/${suffix}`, {
      method: 'GET',
    });
  }

  async createSupportChat(data) {
    return this.request('/support/chats/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async searchUsers(query, limit = 20, storeId = '') {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    if (storeId) params.set('store_id', storeId);
    return this.request(`/support/users/search/?${params.toString()}`, {
      method: 'GET',
    });
  }

  async markSupportMessageUnread(messageId) {
    return this.request(`/support/messages/${messageId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'mark_unread' }),
    });
  }

  async getSupportChat(chatId) {
    return this.request(`/support/chats/${chatId}/`, {
      method: 'GET',
    });
  }

  async updateSupportChat(chatId, data) {
    return this.request(`/support/chats/${chatId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /** Superuser: черновик ответа поддержки по тексту вопроса (OpenAI, дневной кэш контекста в БД). */
  async postSupportAdminAiReplySuggest(question) {
    return this.request('/support/admin/ai-reply-suggest/', {
      method: 'POST',
      body: JSON.stringify({ question: String(question || '').trim() }),
    });
  }

  /** Superuser: same admin text message into each target user's primary support chat. */
  async postSupportAdminBroadcast(payload) {
    return this.request('/support/admin/broadcast/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getSupportChatUnreadCount() {
    return this.request('/support/chats/unread-count/', {
      method: 'GET',
    });
  }

  async sendSupportMessage(chatId, content, translatedContent = '', replyToId = null) {
    const body = {
      chat_id: chatId,
      content: content,
    };
    if (translatedContent) {
      body.translated_content = translatedContent;
    }
    if (replyToId) {
      body.reply_to_id = replyToId;
    }
    return this.request('/support/messages/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Warehouse returns endpoints
  async getWarehouseReturns(params = {}) {
    const queryParams = {
      page: 1,
      size: 50,
      ...params,
    };
    
    // Build query string, handling nested objects and arrays
    const queryStringParts = [];
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] !== undefined && queryParams[key] !== null && queryParams[key] !== '') {
        if (key === 'period' && typeof queryParams[key] === 'object') {
          // Handle period object
          if (queryParams[key].from) {
            queryStringParts.push(`period[from]=${encodeURIComponent(queryParams[key].from)}`);
          }
          if (queryParams[key].to) {
            queryStringParts.push(`period[to]=${encodeURIComponent(queryParams[key].to)}`);
          }
        } else if (key === 'period_type') {
          queryStringParts.push(`period_type=${encodeURIComponent(queryParams[key])}`);
        } else if (key === 'filter' && typeof queryParams[key] === 'object') {
          // Handle filter object
          const filter = queryParams[key];
          
          // Array filters
          if (Array.isArray(filter.client_ids)) {
            filter.client_ids.forEach(id => {
              queryStringParts.push(`filter[client_ids][]=${encodeURIComponent(id)}`);
            });
          }
          if (Array.isArray(filter.marketplaces)) {
            filter.marketplaces.forEach(mp => {
              queryStringParts.push(`filter[marketplaces][]=${encodeURIComponent(mp)}`);
            });
          }
          if (Array.isArray(filter.statuses)) {
            filter.statuses.forEach(status => {
              queryStringParts.push(`filter[statuses][]=${encodeURIComponent(status)}`);
            });
          }
          if (Array.isArray(filter.logistic_services)) {
            filter.logistic_services.forEach(service => {
              queryStringParts.push(`filter[logistic_services][]=${encodeURIComponent(service)}`);
            });
          }
          
          // String filters
          if (filter.search) {
            queryStringParts.push(`filter[search]=${encodeURIComponent(filter.search)}`);
          }
          if (filter.updated_at) {
            queryStringParts.push(`filter[updated_at]=${encodeURIComponent(filter.updated_at)}`);
          }
        } else {
          queryStringParts.push(`${key}=${encodeURIComponent(queryParams[key])}`);
        }
      }
    });
    
    const queryString = queryStringParts.join('&');
    const endpoint = queryString ? `/warehouse/returns/?${queryString}` : '/warehouse/returns/';
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  async uploadSupportMessageAttachment(chatId, file, content = '', translatedContent = '', replyToId = null) {
    const formData = new FormData();
    formData.append('chat_id', chatId);
    formData.append('file', file);
    if (content) {
      formData.append('content', content);
    }
    if (translatedContent) {
      formData.append('translated_content', translatedContent);
    }
    if (replyToId) {
      formData.append('reply_to_id', replyToId);
    }
    return this.requestFormData('/support/messages/upload/', formData);
  }

  async translateMessage(messageId, targetLanguage = 'Russian') {
    return this.request(`/support/messages/${messageId}/translate/`, {
      method: 'POST',
      body: JSON.stringify({ target_language: targetLanguage }),
    });
  }

  async translateText(text, targetLanguage = 'English', sourceLanguage = null) {
    const body = { text, target_language: targetLanguage };
    if (sourceLanguage) {
      body.source_language = sourceLanguage;
    }
    return this.request('/support/translate/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async trackPageVisit(page, sessionId, durationSeconds, startedAt, metadata = {}) {
    return this.request('/metrics/page-visit/', {
      method: 'POST',
      body: JSON.stringify({
        page,
        session_id: sessionId,
        duration_seconds: durationSeconds,
        started_at: startedAt,
        metadata,
      }),
    });
  }

  logout() {
    this.removeToken();
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Build an authenticated URL for media files.
   * Appends the JWT token as a query parameter for browser access.
   * @param {string} url - The media file URL
   * @returns {string} - URL with authentication token
   */
  getAuthenticatedMediaUrl(url) {
    if (!url) return url;
    
    const token = this.getToken();
    if (!token) return url;
    
    // Check if this is an API media URL that needs authentication
    if (url.includes('/api/v1/media/')) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}token=${encodeURIComponent(token)}`;
    }
    
    return url;
  }
}

const api = new ApiService();
export default api;
